import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';
import { useTermsDocuments, type TermsDocument } from '@/hooks/useTermsDocuments';
import { downloadDocument } from '@/lib/downloadDocument';

export default function TermsPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { data: documents } = useTermsDocuments(lang);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (doc: TermsDocument) => {
    setDownloadingId(doc.id);
    try {
      await downloadDocument(doc.file_path);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const getTitle = (doc: TermsDocument) =>
    doc.title_translations?.[lang] || doc.title_translations?.sv || doc.file_name;
  const getDesc = (doc: TermsDocument) =>
    doc.description_translations?.[lang] || doc.description_translations?.sv || '';

  const usageRights = [
    'information.terms.usage.internal',
    'information.terms.usage.noResale',
    'information.terms.usage.noSublicense',
  ];

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('information.terms.title')}
        description={t('meta.terms')}
        path="/information/kopvillkor"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('common.information'), url: `https://postnummerservice.se/${lang}/information/kopvillkor` },
          { name: t('information.terms.title'), url: `https://postnummerservice.se/${lang}/information/kopvillkor` },
        ])}
      />
      <div className="max-w-4xl mx-auto space-y-12">
        {/* Hero Section */}
        <section>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                {t('information.terms.title')}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('information.terms.subtitle')}
              </p>
              {(() => {
                const dates = (documents ?? [])
                  .map((d) => d.updated_at)
                  .filter((s): s is string => Boolean(s));
                if (!dates.length) return null;
                const latest = dates.sort().slice(-1)[0];
                const formatted = new Date(latest).toISOString().slice(0, 10);
                return (
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('information.terms.lastUpdated')}: {formatted}
                  </p>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Usage Rights */}
        <section>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-2">{t('information.terms.usageTitle')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('information.terms.usageDescription')}</p>
            </div>
          </div>
          <div className="pl-16 space-y-3">
            {usageRights.map((right, index) => (
              <Card key={index}>
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>{t(right)}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Liability Disclaimer */}
        <section>
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold mb-2">{t('information.terms.liabilityTitle')}</h2>
              <p className="text-muted-foreground leading-relaxed">{t('information.terms.liabilityDescription')}</p>
            </div>
          </div>
          <div className="pl-16">
            <Card className="border-destructive/20 bg-destructive/5">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{t('information.terms.liabilityNote')}</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Downloadable documents */}
        {documents && documents.length > 0 && (
          <section className="space-y-3">
            {documents.map((doc) => (
              <Card key={doc.id}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-semibold mb-1">{getTitle(doc)}</h3>
                      {getDesc(doc) && (
                        <p className="text-sm text-muted-foreground">{getDesc(doc)}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 flex-shrink-0"
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.id}
                    >
                      <Download className="w-4 h-4" />
                      {downloadingId === doc.id ? '...' : t('information.terms.downloadButton')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
